import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { toast } from 'react-hot-toast'
import type { Rol } from '@/types/database'
import { PlusIcon, EditIcon } from '@/components/shared/Icons'

const TEMPLATES = [
  { nombre: 'Administrador', nivel: 1, desc: 'Acceso total al sistema' },
  { nombre: 'Supervisor', nivel: 1, desc: 'Igual que admin, sin crear usuarios' },
  { nombre: 'Cajero', nivel: 2, desc: 'Ventas, caja y clientes' },
  { nombre: 'Deposito', nivel: 3, desc: 'Stock, productos y compras' },
  { nombre: 'Solo lectura', nivel: 4, desc: 'Solo puede consultar' },
]

const NIVEL_INFO: Record<number, { label: string; pill: string; desc: string }> = {
  1: { label: 'Admin', pill: 'me-pill-acid', desc: 'Acceso total' },
  2: { label: 'Cajero', pill: 'me-pill-ok', desc: 'Ventas y caja' },
  3: { label: 'Depósito', pill: 'me-pill-warn', desc: 'Stock y compras' },
  4: { label: 'Lectura', pill: 'me-pill-neutral', desc: 'Solo consulta' },
}

function useRoles() {
  return useQuery({
    queryKey: ['roles-admin'],
    queryFn: () => api.get<Rol[]>('/api/auth/roles'),
    staleTime: 60_000,
  })
}

function useCrearRol() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { nombre: string; nivel: number }) =>
      api.post('/api/auth/roles', data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles-admin'] })
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

function useEditarRol() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, nombre }: { id: string; nombre: string }) =>
      api.put(`/api/auth/roles/${id}`, { nombre }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['roles-admin'] })
      qc.invalidateQueries({ queryKey: ['roles'] })
    },
  })
}

export default function RolesPage() {
  const { data: roles = [], isLoading } = useRoles()
  const crear = useCrearRol()
  const editar = useEditarRol()

  const [modal, setModal] = useState<'nuevo' | 'editar' | null>(null)
  const [editandoId, setEditandoId] = useState<string | null>(null)
  const [nombre, setNombre] = useState('')
  const [nivel, setNivel] = useState(2)

  function abrirNuevo() { setNombre(''); setNivel(2); setModal('nuevo') }
  function abrirDesdeTemplate(t: typeof TEMPLATES[0]) { setNombre(t.nombre); setNivel(t.nivel); setModal('nuevo') }
  function abrirEditar(r: Rol) { setEditandoId(r.id); setNombre(r.nombre); setModal('editar') }
  function cerrar() { setModal(null); setEditandoId(null); setNombre(''); setNivel(2) }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!nombre.trim()) { toast.error('El nombre es obligatorio'); return }
    try {
      if (modal === 'editar' && editandoId) {
        await editar.mutateAsync({ id: editandoId, nombre })
        toast.success('Rol actualizado')
      } else {
        await crear.mutateAsync({ nombre: nombre.trim(), nivel })
        toast.success('Rol creado')
      }
      cerrar()
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Error al guardar')
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 'var(--me-space-6)' }}>
        <div>
          <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Administracion</div>
          <h1 className="me-page-title">Roles</h1>
        </div>
        <button className="me-btn me-btn-primary" onClick={abrirNuevo}>
          <PlusIcon size={15} /> Nuevo rol
        </button>
      </div>

      <div style={{ fontSize: 13, color: 'var(--me-text-3)', marginBottom: 'var(--me-space-6)', maxWidth: 520 }}>
        Los roles definen el nivel de acceso de cada usuario. El <strong>nivel</strong> determina qué puede hacer en el sistema.
      </div>

      {/* Plantillas rápidas */}
      <div style={{ marginBottom: 'var(--me-space-6)' }}>
        <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-3)' }}>Plantillas rápidas</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {TEMPLATES.map(t => (
            <button key={t.nombre} onClick={() => abrirDesdeTemplate(t)}
              style={{
                display: 'flex', flexDirection: 'column', alignItems: 'flex-start',
                padding: '12px 16px', borderRadius: 10, border: '1px solid var(--me-line)',
                background: 'var(--me-surface)', cursor: 'pointer', gap: 4, minWidth: 140,
                transition: 'border-color var(--me-dur-short), box-shadow var(--me-dur-short)',
              }}
              onMouseOver={e => e.currentTarget.style.borderColor = 'var(--me-acid)'}
              onMouseOut={e => e.currentTarget.style.borderColor = 'var(--me-line)'}
            >
              <span className={`me-pill ${NIVEL_INFO[t.nivel].pill}`} style={t.nivel === 1 ? { color: '#4a5800' } : undefined}>
                Nivel {t.nivel}
              </span>
              <span style={{ fontWeight: 600, fontSize: 13, color: 'var(--me-text)' }}>{t.nombre}</span>
              <span style={{ fontSize: 11, color: 'var(--me-text-4)' }}>{t.desc}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Tabla roles existentes */}
      <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-3)' }}>Roles del negocio</div>
      {isLoading ? (
        <Skeleton />
      ) : (
        <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Nombre del rol</th>
                <th style={{ textAlign: 'center' }}>Nivel</th>
                <th style={{ textAlign: 'center' }}>Acceso</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {roles.map((r, i) => {
                const info = NIVEL_INFO[r.nivel] ?? { label: `N${r.nivel}`, pill: 'me-pill-neutral', desc: '' }
                return (
                  <tr key={r.id} style={{ background: i % 2 !== 0 ? 'var(--me-surface-2)' : 'transparent' }}>
                    <td style={{ fontWeight: 500 }}>{r.nombre}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ fontFamily: 'var(--me-mono)', fontSize: 13, fontWeight: 700, color: 'var(--me-text-3)' }}>
                        {r.nivel}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`me-pill ${info.pill}`} style={r.nivel === 1 ? { color: '#4a5800' } : undefined}>
                        {info.label}
                      </span>
                    </td>
                    <td>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <button className="me-icon-btn" style={{ width: 28, height: 28, borderRadius: 6 }}
                          title="Renombrar" onClick={() => abrirEditar(r)}>
                          <EditIcon size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Leyenda de niveles */}
      <div style={{ marginTop: 'var(--me-space-6)', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
        {Object.entries(NIVEL_INFO).map(([n, info]) => (
          <div key={n} style={{ padding: '12px 16px', borderRadius: 10, background: 'var(--me-surface-2)', border: '1px solid var(--me-line-2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
              <span className={`me-pill ${info.pill}`} style={parseInt(n) === 1 ? { color: '#4a5800' } : undefined}>Nivel {n}</span>
              <span style={{ fontWeight: 600, fontSize: 13 }}>{info.label}</span>
            </div>
            <div style={{ fontSize: 12, color: 'var(--me-text-4)' }}>{info.desc}</div>
          </div>
        ))}
      </div>

      {/* Modal */}
      {modal && (
        <>
          <div onClick={cerrar} style={{ position: 'fixed', inset: 0, zIndex: 40, background: 'rgba(0,0,0,0.28)' }} />
          <div className="me-modal-in" style={{
            position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
            zIndex: 41, width: 380, background: 'var(--me-surface)', borderRadius: 16,
            boxShadow: 'var(--me-shadow-modal)', padding: 'var(--me-space-6)',
          }}>
            <h2 className="me-section-title" style={{ marginBottom: 'var(--me-space-4)' }}>
              {modal === 'editar' ? 'Renombrar rol' : 'Nuevo rol'}
            </h2>
            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--me-space-4)' }}>
              <div>
                <label className="me-label">Nombre del rol *</label>
                <input className="me-input" style={{ width: '100%' }} autoFocus
                  value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Ej: Supervisor, Cajero noche..." />
              </div>
              {modal === 'nuevo' && (
                <div>
                  <label className="me-label">Nivel de acceso *</label>
                  <select className="me-input" style={{ width: '100%' }} value={nivel} onChange={e => setNivel(parseInt(e.target.value))}>
                    <option value={1}>Nivel 1 — Administrador / Supervisor (acceso total)</option>
                    <option value={2}>Nivel 2 — Cajero (ventas, caja, clientes)</option>
                    <option value={3}>Nivel 3 — Depósito (stock, productos, compras)</option>
                    <option value={4}>Nivel 4 — Solo lectura (consulta únicamente)</option>
                  </select>
                </div>
              )}
              <div style={{ display: 'flex', gap: 8 }}>
                <button type="button" className="me-btn me-btn-ghost" onClick={cerrar} style={{ flex: 1 }}>Cancelar</button>
                <button type="submit" className="me-btn me-btn-primary" style={{ flex: 2 }}
                  disabled={crear.isPending || editar.isPending}>
                  {crear.isPending || editar.isPending ? 'Guardando...' : modal === 'editar' ? 'Guardar' : 'Crear rol'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(4)].map((_, i) => (
        <div key={i} style={{ height: 44, background: 'var(--me-surface-3)', borderRadius: 8, opacity: 1 - i * 0.2 }} />
      ))}
    </div>
  )
}
